import React from 'react';
import clsx from 'clsx';
import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, { ContentColumn } from '@/layouts/ContentWrapper/ContentWrapper.component';
import Heading from '@/elements/Heading/Heading.component';
import Text from '@/elements/Text/Text.component';
import { IProps } from './WysiwygBlock.interface';
import './WysiwygBlock.styles.css';
import { decodeBase64Utf8 } from '@/utils/textDecode';

/**
 * WysiwygBlock displays a heading and rich text content, using BlockWrapper for layout.
 * Supports one-column (default) or two-column layout; two-column collapses to single column on mobile.
 * Heading and text are rendered as HTML for full WYSIWYG flexibility. No custom HTML or inline styling required.
 */
const WysiwygBlock: React.FC<IProps> = ({
    columnLayout = 'one-column',
    heading,
    text,
    base64EncodedText = false,
    textColumn2,
    base64EncodedTextColumn2 = false,
    className,
    ...blockWrapperProps
}) => {
    const decodedText = base64EncodedText && !!text ? decodeBase64Utf8(text) : text;
    const decodedTextColumn2 =
        base64EncodedTextColumn2 && !!textColumn2 ? decodeBase64Utf8(textColumn2) : textColumn2;

    const isTwoColumn = columnLayout === 'two-column' && (!!decodedText || !!decodedTextColumn2);

    return (
        <BlockWrapper
            className={clsx('wysiwygBlock', className, isTwoColumn && 'wysiwygBlock--twoColumn')}
            {...(isTwoColumn && { role: 'region', 'aria-label': 'Content in two columns' })}
            {...blockWrapperProps}
        >
            <ContentWrapper>
                {isTwoColumn ? (
                    <>
                        <ContentColumn layout="half" className="wysiwygBlock__column">
                            {heading && (
                                <Heading level={2} className="wysiwygBlock__heading" html={heading} />
                            )}
                            {decodedText && (
                                <Text as="div" className="wysiwygBlock__text" html={decodedText} />
                            )}
                        </ContentColumn>
                        <ContentColumn layout="half" className="wysiwygBlock__column">
                            {decodedTextColumn2 && (
                                <Text
                                    as="div"
                                    className="wysiwygBlock__text"
                                    html={decodedTextColumn2}
                                />
                            )}
                        </ContentColumn>
                    </>
                ) : (
                    <ContentColumn>
                        {heading && (
                            <Heading level={2} className="wysiwygBlock__heading" html={heading} />
                        )}
                        {decodedText && (
                            <Text as="div" className="wysiwygBlock__text" html={decodedText} />
                        )}
                    </ContentColumn>
                )}
            </ContentWrapper>
        </BlockWrapper>
    );
};

export { WysiwygBlock };
